"use client";

import React, { useRef, useState, useEffect } from "react";

import Image from "next/image";
import CornerChip from "@/components/global/ui/CornerChip";

import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import {
  HLSVideoPlayer,
  SanityVideoPlayer,
} from "@/components/ui/SanityVideoPlayer";
import { Dialog, DialogContent } from "@/shadcn/ui/dialog";

import { PageAboutSchema } from "@/sanity/pages/PageAboutTemplate";

import volumeMute from "@/public/icons/Volume--mute--filled.svg";
import volumeUnmute from "@/public/icons/Volume--up--filled.svg";
import maximize from "@/public/icons/Maximize.svg";
import screenEffect from "@/public/about/screen-effect.svg";
import { PageInnerCircleSchema } from "@/sanity/pages/PageInnerCircleTemplate";
import Chip from "../global/ui/Chip";
import Divider from "../global/ui/Divider";
import DividerGreenLine from "../global/ui/DividerGreenLine";
import ChipScrollingBanner from "../global/ui/ChipScrollingBanner";
import GridNoise from "../global/ui/GridNoise";

export default function WhySection({
  data,
  className,
}: {
  data: PageInnerCircleSchema;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "relative bg-white lg:min-h-[122.5rem] flex pt-[5.5rem] lg:pt-[20rem] pb-[12rem] lg:pb-[15rem] px-[2rem] lg:px-0",
        className,
      )}
    >
      <div className="w-[min(183rem,100%)] mx-auto relative p-[3rem] pb-[40rem] lg:px-[8rem] lg:py-[9rem]">
        <Image
          className="hidden md:block absolute left-0 top-0 !w-full !h-full object-cover"
          src={data?.whyImage}
          quality={100}
          alt="Background image"
        />
        <Image
          className="lg:hidden absolute left-0 top-0 !w-full !h-full object-cover"
          src={data?.whyImageMobile}
          quality={100}
          alt="Background image"
        />
        <div className="relative flex flex-col items-start w-[min(69.875rem,100%)]">
          {data?.whyTitle && (
            <div className="w-[85%] lg:w-[125rem] richtext richtext-start prose-headings:not-italic prose-headings:text-white prose-headings:text-[6rem] lg:prose-headings:text-[7rem] prose-headings:font-ivy prose-p:text-white/60">
              <PortableText
                value={data.whyTitle}
                components={richTextComponent}
              />
            </div>
          )}
          <DividerGreenLine
            className="w-full my-[3rem]"
            greenLineClassName="w-[20.92rem] left-0 translate-x-0"
          />
          {data?.whyText && (
            <div
              className="mb-[4rem] lg:mb-[10rem] richtext richtext-start prose-em:font-ivy prose-headings:text-white prose-headings:text-[2.25rem] prose-headings:uppercase prose-headings:font-light
              prose-p:text-white/60 prose-p:text-[2.25rem] prose-p:font-light prose-p:leading-[150%]"
            >
              <PortableText
                value={data.whyText}
                components={richTextComponent}
              />
            </div>
          )}
          {data?.whyBottomText && (
            <div
              className="richtext richtext-start prose-headings:text-white prose-headings:text-[2.25rem] prose-headings:font-geist
              prose-p:text-white/60 prose-p:text-[2.25rem] prose-p:font-light prose-p:leading-[150%]
              prose-li:marker:text-[#D9F9B9] prose-ul:[list-style-type:square]
              prose-li:text-white/60 prose-li:text-[2.25rem] prose-li:font-light prose-li:leading-[150%]"
            >
              <PortableText
                value={data.whyBottomText}
                components={richTextComponent}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
